import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid } from '@mui/material';
import { utils, writeFileXLSX } from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toast } from 'react-toastify';

import {
  getClientReport,
  getDayWiseHourReport,
  getEmployeeReport,
  getProjectReport,
  getTaskNoteReport,
  getTaskTypeReport,
  getTimeSheetReport,
  getPlannedVsActualReport,
} from '../../services/reportService';
import { getTeamMembers } from '../../services/teamService';

import { GRAPH_LIST, INITIAL_REPORT_FILTER_STATE, INITIAL_REPORT_STATE } from './constants';
import { dateToDDMMYYY, dateToTime, getTimeZone, secondsToTime } from '../../utils/timeCalculation';
import { isAdmin, isProjectQaReportPlannedVsActual } from '../../utils/share';

import { DayWiseHourReport, DoughnutReportView, TimeSheetReport } from './charts';
import { ExportActions, FilterBox, InfoSection } from './components';
import Loading from '../../layouts/loading';

import { useStyles } from './Style/reportPageStyles';
import './Style/report.css';
import GenerateChartData from './common/generateChartData';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import PlannedVsActualReportDataTable from './components/PlannedVsActualReport/PlannedVsActualReportDataTable';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export default function ReportPage() {
  const classes = useStyles();
  /* get state data and export to XLSX */
  const componentRef = useRef();

  const currentUser = useSelector((state) => state?.login?.user);

  const [filter, setFilter] = useState(INITIAL_REPORT_FILTER_STATE);
  const [report, setReport] = useState(INITIAL_REPORT_STATE);

  const [totalTime, setTotalTime] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [members, setMembers] = useState();

  const handleSearchClick = (values) => {
    const newFilter = {};
    newFilter.dateRange = values?.dateRange;
    newFilter.selectedClientIds = values?.clientList?.map((client) => client?.id);
    newFilter.selectedProjectIds = values?.projectList?.map((project) => project?.id);
    newFilter.selectedProjectStatusList = values?.projectStatusList;
    newFilter.selectedTaskStatusList = values?.taskStatusList;
    newFilter.selectedEmployeeIds = values?.employeeList?.map((employee) => employee?.id);
    newFilter.selectedGraphList = values?.graphList;
    newFilter.taskNote = values?.taskNote;
    newFilter.selectedTaskTypes = values?.taskTypeList;
    newFilter.selectedBillableIds = values?.billableTypeList?.map((billable) => billable?.id);
    setFilter(newFilter);
  };

  const exportFile = async () => {
    try {
      setLoading(true);
      const timeSheetData = report?.timeSheetReport?.table ?? [];

      const data = timeSheetData?.map((item) => ({
        employee: item?.tasks?.user?.userName,
        project: item?.tasks?.projects?.projectName,
        client: item?.tasks?.projects?.clients?.clientName,
        task: item?.tasks?.taskName,
        taskType: item?.tasks?.taskType,
        start: `${dateToDDMMYYY(item?.startTime)} ${dateToTime(item?.startTime)}`,
        end: `${dateToDDMMYYY(item?.startTime)} ${item?.stopTime ? dateToTime(item?.stopTime) : 'Running'}`,
        durationInSecond: item?.duration,
        duration: secondsToTime(item?.duration),
        status: item?.tasks?.status,
        plannedInSecond: (item?.tasks?.plannedHours || 0) * 60 * 60,
        'planned-duration': (item?.tasks?.plannedHours || 0) * 60 * 60 - (item?.duration || 0),
      }));

      const ws = utils.json_to_sheet(data);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Data');
      writeFileXLSX(wb, 'report.xlsx');
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        if (!isAdmin()) {
          const getTeamMembersResponse = await getTeamMembers();
          const allowedList = getTeamMembersResponse?.data?.data ?? [];
          allowedList.push(currentUser?.id);
          setMembers(allowedList);
        }
      } catch (error) {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      }
    };

    fetchTeamMembers();
  }, [currentUser?.id]);

  useEffect(() => {
    let totalTime = 0;
    const fetchDailyReport = async (employeeList) => {
      const response = await getDayWiseHourReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );

      setReport((prev) => ({ ...prev, dayWiseHourReport: response?.dailyHours }));
      setTotalTime(response?.totalTime);
      totalTime = response?.totalTime;
    };

    const fetchClientReport = async (employeeList) => {
      const responseData = await getClientReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const clientReport = {};
      clientReport.table = responseData?.clients ?? [];
      clientReport.graph =
        responseData?.clients.length > 0 ? GenerateChartData(responseData?.clients, 'clientName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, clientReport }));
    };

    const fetchProjectReport = async (employeeList) => {
      const responseData = await getProjectReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const projectReport = {};
      projectReport.table = responseData?.projects ?? [];
      projectReport.graph =
        responseData?.projects.length > 0 ? GenerateChartData(responseData?.projects, 'projectName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, projectReport }));
    };

    const fetchEmployeeReport = async (employeeList) => {
      const responseData = await getEmployeeReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const employeeReport = {};
      employeeReport.table = responseData?.users ?? [];
      employeeReport.graph =
        responseData?.users.length > 0 ? GenerateChartData(responseData?.users, 'userName', totalTime, 10) : [];
      setReport((prev) => ({ ...prev, employeeReport }));
    };

    const fetchTaskNoteReport = async (employeeList) => {
      const responseData = await getTaskNoteReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const taskNoteReport = {};
      taskNoteReport.table = responseData?.tasks ?? [];
      taskNoteReport.graph =
        responseData?.tasks.length > 0 ? GenerateChartData(responseData?.tasks, 'taskName', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, taskNoteReport }));
    };

    const fetchTaskTypeReport = async (employeeList) => {
      const responseData = await getTaskTypeReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      const taskTypeReport = {};
      taskTypeReport.table = responseData?.tasks ?? [];
      taskTypeReport.graph =
        responseData?.tasks.length > 0 ? GenerateChartData(responseData?.tasks, 'taskType', totalTime, 6) : [];
      setReport((prev) => ({ ...prev, taskTypeReport }));
    };

    const fetchTimeSheetReport = async (employeeList) => {
      const responseData = await getTimeSheetReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      setReport((prev) => ({ ...prev, timeSheetReport: { table: responseData?.timeSheet } }));
    };

    const fetchPlannedVsActualReport = async (employeeList) => {
      const responseData = await getPlannedVsActualReport(
        filter?.selectedClientIds,
        filter?.selectedProjectIds,
        filter?.selectedProjectStatusList,
        filter?.selectedTaskStatusList,
        employeeList,
        filter?.taskNote,
        filter?.dateRange,
        filter?.selectedTaskTypes,
        filter?.selectedBillableIds,
        getTimeZone()
      );
      console.log(responseData);
      setReport((prev) => ({ ...prev, plannedVsActualReport: { table: responseData?.plannedVsActualReport } }));
    };

    const fetchReportData = async () => {
      setLoading(true);
      if (isAdmin() || members) {
        try {
          const employeeList = filter?.selectedEmployeeIds?.length || isAdmin() ? filter?.selectedEmployeeIds : members;
          await fetchDailyReport(employeeList);

          const promises = [];

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Client Report')) {
            promises.push(fetchClientReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Project Report')) {
            promises.push(fetchProjectReport(employeeList));
          }

          if (
            isAdmin() &&
            (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Employee Report'))
          ) {
            promises.push(fetchEmployeeReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Task Note Report')) {
            promises.push(fetchTaskNoteReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Timesheet Report')) {
            promises.push(fetchTimeSheetReport(employeeList));
          }

          if (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Task Type Report')) {
            promises.push(fetchTaskTypeReport(employeeList));
          }

          if (
            (isAdmin() || isProjectQaReportPlannedVsActual()) &&
            (filter?.selectedGraphList.length === 0 || filter?.selectedGraphList.includes('Planned vs Actual Report'))
          ) {
            promises.push(fetchPlannedVsActualReport(employeeList));
          }

          await Promise.all(promises);
        } catch (error) {
          toast.error(error?.response?.data?.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchReportData();
  }, [filter, members]);

  const renderReportComponent = (graph) => {
    switch (graph) {
      case 'Client Report':
        return (
          <DoughnutReportView
            data={report?.clientReport}
            reportHeading="Client Report"
            tableTitle="Client"
            tableDataColumn="clientName"
          />
        );
      case 'Project Report':
        return (
          <DoughnutReportView
            data={report?.projectReport}
            reportHeading="Project Report"
            tableTitle="Project"
            tableDataColumn="projectName"
          />
        );
      case 'Employee Report':
        return isAdmin() ? (
          <DoughnutReportView
            data={report?.employeeReport}
            reportHeading="Employee Report"
            tableTitle="Employee"
            tableDataColumn="userName"
          />
        ) : null;
      case 'Task Note Report':
        return (
          <DoughnutReportView
            data={report?.taskNoteReport}
            reportHeading="Task Note Report"
            tableTitle="Task"
            tableDataColumn="taskName"
          />
        );
      case 'Timesheet Report':
        return <TimeSheetReport data={report?.timeSheetReport} />;

      case 'Planned vs Actual Report':
        console.log(filter?.dateRange);
        return (
          <PlannedVsActualReportDataTable
            data={report?.plannedVsActualReport?.table}
            startDate={filter?.dateRange?.selection?.startDate}
            endDate={filter?.dateRange?.selection?.endDate}
          />
        );
      default:
        return null;
    }
  };

  const chartList = filter?.selectedGraphList.length > 0 ? filter?.selectedGraphList : GRAPH_LIST;

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Report',
      path: '',
    },
  ];

  return (
    <>
      <Loading open={isLoading} />
      <Grid container>
        <Grid item xs={12}>
          <BreadCrumb breadcrumbs={breadcrumbs} />
        </Grid>

        <Grid item xs={12}>
          <FilterBox handleSearchClick={handleSearchClick} />
        </Grid>

        <Box className={classes.toolbar}>
          <InfoSection totalTime={totalTime} dateRange={filter?.dateRange} />
          <ExportActions graphContainerRef={componentRef} exportFile={exportFile} />
        </Box>

        <Grid container ref={componentRef} style={{ margin: 5 }}>
          {chartList.includes('Time Line Report') && <DayWiseHourReport data={report.dayWiseHourReport} />}

          {chartList.map((graph) => (
            <React.Fragment key={graph}>{renderReportComponent(graph)}</React.Fragment>
          ))}
        </Grid>
      </Grid>
    </>
  );
}
